// Per-mode forms for the ten update_ucc sub-object modes wired at
// POST /admin/bse2/ucc/update. Each mode maps 1:1 onto a builder function in
// bse_v2/ucc_builder.py -- the exported empty*Spec() factories and buildSpec()
// below use the exact kwarg names those builders expect. Read the builder's
// own docstring before changing a field name here; a mismatch produces a
// confusing runtime failure on the backend rather than a client-side validation error.
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { CheckField, FormField, FormSelect } from "@/components/bse/BseUatForm";
import { BANK_TYPE, CONTACT_TYPE, GENDER, HOLDING, INCOME, NOM_RELATION, OCCUPATION, OWNER, PEP } from "@/data/bseUccV203";

// Stable row ids for every repeatable list in this file. Never key React lists
// by array index here -- removing a row would otherwise reassign a surviving
// row's identity to the wrong index and corrupt its form state mid-edit.
let rowSeq = 0;
export function nextRowId() {
  rowSeq += 1;
  return `ucc-row-${rowSeq}`;
}

const HOLDER_RANKS = [
  { value: "1", label: "1 — Primary" },
  { value: "2", label: "2 — Second" },
  { value: "3", label: "3 — Third" },
  { value: "-1", label: "-1 — Guardian" },
];

function readFileAsDoc(file, onReady) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => onReady({
    file_name: file.name,
    file_size: Math.ceil(file.size / 1024),
    file_blob: String(reader.result).split(",").pop(),
  });
  reader.readAsDataURL(file);
}

function RowShell({ title, onRemove, children }) {
  return (
    <div className="space-y-3 rounded-lg border border-[var(--glass-border)] p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-[var(--ink-muted)]">{title}</p>
        {onRemove && <Button type="button" size="sm" variant="ghost" onClick={onRemove}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
      {children}
    </div>
  );
}

function AddRowButton({ label, onClick }) {
  return <Button type="button" size="sm" variant="outline" onClick={onClick}><Plus className="mr-1 h-3.5 w-3.5" />{label}</Button>;
}

function DocAttachment({ row, onChange, typeOptions }) {
  if (!row.doc) {
    return <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...row, doc: { identifier_type: typeOptions[0].value, file_name: "", file_size: "", file_blob: "" } })}>
      <Plus className="mr-1 h-3.5 w-3.5" />Attach proof document
    </Button>;
  }
  return (
    <div className="rounded-lg border border-dashed border-[var(--glass-border)] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium">Proof document (optional)</span>
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...row, doc: null })}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormSelect label="Document type" value={row.doc.identifier_type} options={typeOptions}
          onChange={(e) => onChange({ ...row, doc: { ...row.doc, identifier_type: e.target.value } })} />
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-[var(--ink-muted)]">File</Label>
          <input type="file" className="block w-full rounded-md border p-2 text-xs"
            onChange={(e) => readFileAsDoc(e.target.files?.[0], (doc) => onChange({ ...row, doc: { ...row.doc, ...doc } }))} />
          {row.doc.file_name && <p className="text-[11px] text-emerald-700">{row.doc.file_name} ready to send</p>}
        </div>
      </div>
    </div>
  );
}

// Shared add[]/delete[] editor for bank, depository and identifier modes -- all
// three wire an {add:[...], delete:[...]} diff straight onto the update_ucc envelope.
function AddDeleteEditor({ value, onChange, addLabel, deleteLabel, makeRow, renderRow }) {
  const mutate = (key, updater) => onChange({ ...value, [key]: updater(value[key] || []) });
  const renderList = (key, label) => (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{label}</h3>
        <AddRowButton label="Add row" onClick={() => mutate(key, (rows) => [...rows, makeRow()])} />
      </div>
      <div className="space-y-3">
        {(value[key] || []).map((row, index) => (
          <RowShell key={row._id} title={`${label} ${index + 1}`} onRemove={() => mutate(key, (rows) => rows.filter((r) => r._id !== row._id))}>
            {renderRow(row, (next) => mutate(key, (rows) => rows.map((r) => (r._id === row._id ? next : r))))}
          </RowShell>
        ))}
        {!(value[key] || []).length && <p className="text-xs text-[var(--ink-muted)]">Nothing queued here yet.</p>}
      </div>
    </div>
  );
  return <div className="space-y-5">{renderList("add", addLabel)}{renderList("delete", deleteLabel)}</div>;
}

// ---------------------------------------------------------------- bank (update_bank)
// update_bank(client_code, *, member_code="", add=None, delete=None)
// add/delete rows: {ifsc, acc_num, acc_type, account_owner, doc}
export function emptyBankRow() {
  return { _id: nextRowId(), ifsc: "", acc_num: "", acc_type: "SB", account_owner: "SELF", doc: null };
}
export function emptyBankSpec() {
  return { add: [emptyBankRow()], delete: [] };
}
const BANK_DOC_TYPES = [
  { value: "cancel_cheque", label: "Cancelled cheque" },
  { value: "bank_statement", label: "Bank statement" },
  { value: "bank_log", label: "Bank log" },
];
export function BankForm({ value, onChange }) {
  return (
    <AddDeleteEditor value={value} onChange={onChange} addLabel="Bank accounts to add" deleteLabel="Bank accounts to remove"
      makeRow={emptyBankRow}
      renderRow={(row, change) => (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="IFSC" value={row.ifsc} maxLength={11} onChange={(e) => change({ ...row, ifsc: e.target.value.toUpperCase() })} />
            <FormField label="Account number" value={row.acc_num} onChange={(e) => change({ ...row, acc_num: e.target.value })} />
            <FormSelect label="Account type" value={row.acc_type} options={BANK_TYPE} onChange={(e) => change({ ...row, acc_type: e.target.value })} />
            <FormSelect label="Account owner" value={row.account_owner} options={OWNER} onChange={(e) => change({ ...row, account_owner: e.target.value })} />
          </div>
          <DocAttachment row={row} onChange={change} typeOptions={BANK_DOC_TYPES} />
        </div>
      )} />
  );
}

// ---------------------------------------------------------------- depository (update_depository)
// update_depository(client_code, *, member_code="", add=None, delete=None)
// add/delete rows: {code, dp_id, client_id, bank_account, account_owner, cmbp_id, doc}
export function emptyDepositoryRow() {
  return { _id: nextRowId(), code: "NSDL", dp_id: "", client_id: "", bank_account: "", account_owner: "SELF", cmbp_id: "", doc: null };
}
export function emptyDepositorySpec() {
  return { add: [emptyDepositoryRow()], delete: [] };
}
export function DepositoryForm({ value, onChange }) {
  return (
    <AddDeleteEditor value={value} onChange={onChange} addLabel="Depository accounts to add" deleteLabel="Depository accounts to remove"
      makeRow={emptyDepositoryRow}
      renderRow={(row, change) => (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <FormSelect label="Depository" value={row.code} options={[{ value: "NSDL", label: "NSDL" }, { value: "CDSL", label: "CDSL" }]}
              onChange={(e) => change({ ...row, code: e.target.value })} />
            <FormField label="DP ID" value={row.dp_id} maxLength={8} onChange={(e) => change({ ...row, dp_id: e.target.value.toUpperCase() })} />
            <FormField label="Client ID" value={row.client_id} maxLength={8} onChange={(e) => change({ ...row, client_id: e.target.value })} />
            <FormField label="Linked bank account number" value={row.bank_account} onChange={(e) => change({ ...row, bank_account: e.target.value })} />
            <FormSelect label="Account owner" value={row.account_owner} options={OWNER} onChange={(e) => change({ ...row, account_owner: e.target.value })} />
            {row.code === "NSDL" && <FormField label="CMBP ID" value={row.cmbp_id} maxLength={8} onChange={(e) => change({ ...row, cmbp_id: e.target.value })} help="NSDL only; CDSL rows must not carry this." />}
          </div>
          <DocAttachment row={row} onChange={change} typeOptions={[{ value: "cml_copy", label: "CML copy" }]} />
        </div>
      )} />
  );
}

// ---------------------------------------------------------------- contact (update_contact)
// update_contact(client_code, *, member_code="", holder_rank="1", occ_code="02", contact)
// contact: {phone, email, contact_type, extension, fax_no}
export function emptyContactSpec() {
  return { holder_rank: "1", occ_code: "02", contact: { phone: "", email: "", contact_type: "PR", extension: "", fax_no: "" } };
}
export function ContactForm({ value, onChange }) {
  const setTop = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const setContact = (key) => (e) => onChange({ ...value, contact: { ...value.contact, [key]: e.target.value } });
  const landline = ["RE", "OF"].includes(value.contact.contact_type);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <FormSelect label="Holder rank" value={value.holder_rank} options={HOLDER_RANKS} onChange={setTop("holder_rank")} />
        <FormSelect label="Occupation" value={value.occ_code} options={OCCUPATION} onChange={setTop("occ_code")} />
        <FormSelect label="Contact type" value={value.contact.contact_type} options={CONTACT_TYPE} onChange={setContact("contact_type")} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Phone / mobile" value={value.contact.phone} onChange={setContact("phone")} />
        <FormField label="Email" type="email" value={value.contact.email} onChange={setContact("email")} />
        {landline && <FormField label="Extension" value={value.contact.extension} onChange={setContact("extension")} help="Required for residential/office contact types." />}
        {landline && <FormField label="Fax number" value={value.contact.fax_no} onChange={setContact("fax_no")} help="Required for residential/office contact types." />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- fatca (update_fatca)
// update_fatca(client_code, *, member_code="", holder_rank="1", pan, person, fatca_extra=None)
// person: {first_name, middle_name, last_name, dob}
// fatca_extra keys read by the builder: place_of_birth, occ_code, income_slab,
// country_of_birth, politically_exposed, investor_type, tax_status (all popped
// before .update(extra) merges anything left over -- e.g. father_name/spouse_name
// -- straight onto the FATCA wire entry).
export function emptyFatcaSpec() {
  return {
    holder_rank: "1", pan: "",
    person: { first_name: "", middle_name: "", last_name: "", dob: "", allow_blank_dob: false },
    fatca_extra: {
      place_of_birth: "", occ_code: "01", income_slab: "31", country_of_birth: "IND",
      politically_exposed: "N", investor_type: "Individual", tax_status: "Individual",
      father_name: "", spouse_name: "",
    },
  };
}
export function FatcaForm({ value, onChange }) {
  const setTop = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const setPerson = (key) => (e) => onChange({ ...value, person: { ...value.person, [key]: e.target.value } });
  const setExtra = (key) => (e) => onChange({ ...value, fatca_extra: { ...value.fatca_extra, [key]: e.target.value } });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <FormSelect label="Holder rank" value={value.holder_rank} options={HOLDER_RANKS} onChange={setTop("holder_rank")} />
        <FormField label="PAN" value={value.pan} maxLength={10} onChange={(e) => onChange({ ...value, pan: e.target.value.toUpperCase() })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="First name" value={value.person.first_name} onChange={setPerson("first_name")} />
        <FormField label="Middle name" value={value.person.middle_name} onChange={setPerson("middle_name")} />
        <FormField label="Last name" value={value.person.last_name} onChange={setPerson("last_name")} />
        <FormField label="Date of birth" type="date" value={value.person.dob} disabled={value.person.allow_blank_dob} onChange={setPerson("dob")} />
      </div>
      <CheckField label="Date of birth unknown (send blank, matching the official BSE sample rather than a fabricated date)"
        checked={value.person.allow_blank_dob}
        onChange={(checked) => onChange({ ...value, person: { ...value.person, allow_blank_dob: checked, dob: checked ? "" : value.person.dob } })} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <FormField label="Place of birth" value={value.fatca_extra.place_of_birth} onChange={setExtra("place_of_birth")} />
        <FormField label="Country of birth ISO-3" value={value.fatca_extra.country_of_birth} maxLength={3}
          onChange={(e) => onChange({ ...value, fatca_extra: { ...value.fatca_extra, country_of_birth: e.target.value.toUpperCase() } })} />
        <FormSelect label="Occupation" value={value.fatca_extra.occ_code} options={OCCUPATION} onChange={setExtra("occ_code")} />
        <FormSelect label="Income slab" value={value.fatca_extra.income_slab} options={INCOME} onChange={setExtra("income_slab")} />
        <FormSelect label="Political exposure" value={value.fatca_extra.politically_exposed} options={PEP} onChange={setExtra("politically_exposed")} />
        <FormSelect label="Investor type" value={value.fatca_extra.investor_type}
          options={[{ value: "Individual", label: "Individual" }, { value: "Non-Individual", label: "Non-Individual" }]} onChange={setExtra("investor_type")} />
        <FormField label="Tax status text" value={value.fatca_extra.tax_status} onChange={setExtra("tax_status")} />
        <FormField label="Father's name (needed without PAN)" value={value.fatca_extra.father_name} onChange={setExtra("father_name")} />
        <FormField label="Spouse's name (needed without PAN)" value={value.fatca_extra.spouse_name} onChange={setExtra("spouse_name")} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- nominee (update_nominee)
// update_nominee(client_code, *, member_code="", holder_rank="1", occ_code="03", nominees, nominee_soa=False)
// nominees[]: {first_name, last_name, dob, gender, phone (required), email, percent,
//              relation, is_minor, pan, comm:{line1,city,state,pincode} (required),
//              guardian:{first_name,last_name,pan,dob} (only when is_minor)}
export function emptyNomineeRow() {
  return {
    _id: nextRowId(), first_name: "", last_name: "", dob: "", gender: "M", phone: "", email: "",
    percent: "100", relation: "5", is_minor: false, pan: "",
    comm: { line1: "", city: "", state: "", pincode: "" },
    guardian: { first_name: "", last_name: "", pan: "", dob: "" },
  };
}
export function emptyNomineeSpec() {
  return { holder_rank: "1", occ_code: "03", nominee_soa: false, nominees: [emptyNomineeRow()] };
}
function NomineeRowFields({ row, onChange }) {
  const set = (key) => (e) => onChange({ ...row, [key]: e.target.value });
  const setComm = (key) => (e) => onChange({ ...row, comm: { ...row.comm, [key]: e.target.value } });
  const setGuardian = (key) => (e) => onChange({ ...row, guardian: { ...row.guardian, [key]: e.target.value } });
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="First name" value={row.first_name} onChange={set("first_name")} />
        <FormField label="Last name" value={row.last_name} onChange={set("last_name")} />
        <FormField label="Date of birth" type="date" value={row.dob} onChange={set("dob")} help="Required — update_nominee's own person builder does not allow a blank nominee dob (only the guardian's may be blank)." />
        <FormSelect label="Gender" value={row.gender} options={GENDER} onChange={set("gender")} />
        <FormField label="Phone" value={row.phone} onChange={set("phone")} help="Required by the BSE nominee wire sample." />
        <FormField label="Email (optional)" type="email" value={row.email} onChange={set("email")} />
        <FormField label="Allocation %" type="number" min="0" max="100" value={row.percent} onChange={set("percent")} />
        <FormSelect label="Relationship" value={row.relation} options={NOM_RELATION} onChange={set("relation")} />
        <FormField label="PAN (optional)" value={row.pan} maxLength={10} onChange={(e) => onChange({ ...row, pan: e.target.value.toUpperCase() })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="Address line 1" value={row.comm.line1} onChange={setComm("line1")} />
        <FormField label="City" value={row.comm.city} onChange={setComm("city")} />
        <FormField label="State" value={row.comm.state} onChange={setComm("state")} />
        <FormField label="Pincode" value={row.comm.pincode} onChange={setComm("pincode")} />
      </div>
      <CheckField label="Nominee is a minor" checked={row.is_minor} onChange={(checked) => onChange({ ...row, is_minor: checked })} />
      {row.is_minor && (
        <div className="grid gap-3 rounded-lg bg-[var(--surface-2)] p-3 sm:grid-cols-2 lg:grid-cols-4">
          <FormField label="Guardian first name" value={row.guardian.first_name} onChange={setGuardian("first_name")} />
          <FormField label="Guardian last name" value={row.guardian.last_name} onChange={setGuardian("last_name")} />
          <FormField label="Guardian date of birth (optional)" type="date" value={row.guardian.dob} onChange={setGuardian("dob")} />
          <FormField label="Guardian PAN" value={row.guardian.pan} maxLength={10}
            onChange={(e) => onChange({ ...row, guardian: { ...row.guardian, pan: e.target.value.toUpperCase() } })} />
        </div>
      )}
    </div>
  );
}
export function NomineeForm({ value, onChange }) {
  const setTop = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const updateRow = (id, next) => onChange({ ...value, nominees: value.nominees.map((r) => (r._id === id ? next : r)) });
  const removeRow = (id) => onChange({ ...value, nominees: value.nominees.filter((r) => r._id !== id) });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormSelect label="Holder rank" value={value.holder_rank} options={HOLDER_RANKS} onChange={setTop("holder_rank")} />
        <FormSelect label="Occupation" value={value.occ_code} options={OCCUPATION} onChange={setTop("occ_code")} />
      </div>
      <CheckField label="Show nominee in statement of account" checked={value.nominee_soa} onChange={(checked) => onChange({ ...value, nominee_soa: checked })} />
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Nominees</h3>
        {value.nominees.length < 5 && <AddRowButton label="Add nominee" onClick={() => onChange({ ...value, nominees: [...value.nominees, emptyNomineeRow()] })} />}
      </div>
      <div className="space-y-4">
        {value.nominees.map((row, index) => (
          <RowShell key={row._id} title={`Nominee ${index + 1}`} onRemove={() => removeRow(row._id)}>
            <NomineeRowFields row={row} onChange={(next) => updateRow(row._id, next)} />
          </RowShell>
        ))}
      </div>
      {!value.nominees.length && <p className="text-xs text-[var(--ink-muted)]">At least one nominee is required.</p>}
    </div>
  );
}

// ---------------------------------------------------------------- comm_addr (update_comm_addr)
// update_comm_addr(client_code, *, member_code="", address)
// address: {line1, line2, line3, pincode, city, state, country}
export function emptyCommAddrSpec() {
  return { address: { line1: "", line2: "", line3: "", pincode: "", city: "", state: "", country: "IND" } };
}
export function CommAddrForm({ value, onChange }) {
  const set = (key) => (e) => onChange({ address: { ...value.address, [key]: e.target.value } });
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <FormField label="Address line 1" value={value.address.line1} onChange={set("line1")} />
      <FormField label="Address line 2" value={value.address.line2} onChange={set("line2")} />
      <FormField label="Address line 3" value={value.address.line3} onChange={set("line3")} />
      <FormField label="City" value={value.address.city} onChange={set("city")} />
      <FormField label="State" value={value.address.state} onChange={set("state")} />
      <FormField label="Pincode" value={value.address.pincode} onChange={set("pincode")} />
      <FormField label="Country" value={value.address.country} onChange={set("country")} help="Defaults to IND when left blank." />
    </div>
  );
}

// ---------------------------------------------------------------- foreign_addr (update_foreign_addr)
// update_foreign_addr(client_code, *, member_code="", address)
// address passed straight to make_foreign_addr(**address): {line1, line2, line3, city, state, country, pincode}
export function emptyForeignAddrSpec() {
  return { address: { line1: "", line2: "", line3: "", city: "", state: "", country: "", pincode: "" } };
}
export function ForeignAddrForm({ value, onChange }) {
  const set = (key) => (e) => onChange({ address: { ...value.address, [key]: e.target.value } });
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <FormField label="Address line 1" value={value.address.line1} maxLength={40} onChange={set("line1")} />
      <FormField label="Address line 2" value={value.address.line2} maxLength={40} onChange={set("line2")} />
      <FormField label="Address line 3" value={value.address.line3} maxLength={40} onChange={set("line3")} />
      <FormField label="City" value={value.address.city} onChange={set("city")} />
      <FormField label="State / province" value={value.address.state} onChange={set("state")} />
      <FormField label="Country ISO-3" value={value.address.country} maxLength={3} placeholder="USA"
        onChange={(e) => onChange({ address: { ...value.address, country: e.target.value.toUpperCase() } })} />
      <FormField label="Postal code" value={value.address.pincode} onChange={set("pincode")} />
    </div>
  );
}

// ---------------------------------------------------------------- holder (update_holder)
// update_holder(client_code, *, member_code="", holder_rank="1", occ_code="01", pan="",
//                person, contact=None, include_fatca=False, fatca_extra=None)
// Note fatca_extra keys here are the real make_fatca() kwargs (city, pep, ...) --
// a DIFFERENT alias set than update_fatca's own fatca_extra (place_of_birth,
// politically_exposed, ...) above. Do not copy field names across the two modes.
export function emptyHolderSpec() {
  return {
    holder_rank: "1", occ_code: "01", pan: "",
    person: { first_name: "", middle_name: "", last_name: "", dob: "", gender: "M", allow_blank_dob: false },
    contact: { phone: "", email: "" },
    include_fatca: false,
    fatca_extra: {
      city: "", occ_code: "01", income_slab: "31", country_of_birth: "IND", pep: "N",
      investor_type: "Individual", tax_status: "Individual", father_name: "",
    },
  };
}
export function HolderForm({ value, onChange }) {
  const setTop = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const setPerson = (key) => (e) => onChange({ ...value, person: { ...value.person, [key]: e.target.value } });
  const setContact = (key) => (e) => onChange({ ...value, contact: { ...value.contact, [key]: e.target.value } });
  const setExtra = (key) => (e) => onChange({ ...value, fatca_extra: { ...value.fatca_extra, [key]: e.target.value } });
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <FormSelect label="Holder rank" value={value.holder_rank} options={HOLDER_RANKS} onChange={setTop("holder_rank")} />
        <FormSelect label="Occupation" value={value.occ_code} options={OCCUPATION} onChange={setTop("occ_code")} />
        <FormField label="PAN" value={value.pan} maxLength={10} onChange={(e) => onChange({ ...value, pan: e.target.value.toUpperCase() })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="First name" value={value.person.first_name} onChange={setPerson("first_name")} />
        <FormField label="Middle name" value={value.person.middle_name} onChange={setPerson("middle_name")} />
        <FormField label="Last name" value={value.person.last_name} onChange={setPerson("last_name")} />
        <FormField label="Date of birth" type="date" value={value.person.dob} disabled={value.person.allow_blank_dob} onChange={setPerson("dob")} />
        <FormSelect label="Gender" value={value.person.gender} options={GENDER} onChange={setPerson("gender")} />
      </div>
      <CheckField label="Date of birth unknown (send blank)" checked={value.person.allow_blank_dob}
        onChange={(checked) => onChange({ ...value, person: { ...value.person, allow_blank_dob: checked, dob: checked ? "" : value.person.dob } })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Phone (optional; required if email is set)" value={value.contact.phone} onChange={setContact("phone")}
          help="make_contact always requires a phone — an email-only contact cannot be sent." />
        <FormField label="Email (optional)" type="email" value={value.contact.email} onChange={setContact("email")} />
      </div>
      <CheckField label="Also send a FATCA declaration for this holder" checked={value.include_fatca}
        onChange={(checked) => onChange({ ...value, include_fatca: checked })} />
      {value.include_fatca && (
        <div className="grid gap-3 rounded-lg bg-[var(--surface-2)] p-3 sm:grid-cols-2 lg:grid-cols-3">
          <FormField label="Place of birth (city)" value={value.fatca_extra.city} onChange={setExtra("city")} />
          <FormField label="Country of birth ISO-3" value={value.fatca_extra.country_of_birth} maxLength={3}
            onChange={(e) => onChange({ ...value, fatca_extra: { ...value.fatca_extra, country_of_birth: e.target.value.toUpperCase() } })} />
          <FormSelect label="Occupation" value={value.fatca_extra.occ_code} options={OCCUPATION} onChange={setExtra("occ_code")} />
          <FormSelect label="Income slab" value={value.fatca_extra.income_slab} options={INCOME} onChange={setExtra("income_slab")} />
          <FormSelect label="Political exposure" value={value.fatca_extra.pep} options={PEP} onChange={setExtra("pep")} />
          <FormField label="Tax status text" value={value.fatca_extra.tax_status} onChange={setExtra("tax_status")} />
          <FormField label="Father's name (needed without PAN)" value={value.fatca_extra.father_name} onChange={setExtra("father_name")} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- identifier (update_identifier)
// update_identifier(client_code, *, member_code="", add=None, delete=None)
// Rows are sent verbatim -- the builder does no shaping, so any wire identifier
// fields the operator fills (type/number/dates/file) travel unchanged.
export function emptyIdentifierRow() {
  return { _id: nextRowId(), identifier_type: "", identifier_number: "", issue_date: "", expiry_date: "", file_name: "", file_size: "", file_blob: "" };
}
export function emptyIdentifierSpec() {
  return { add: [emptyIdentifierRow()], delete: [] };
}
export function IdentifierForm({ value, onChange }) {
  return (
    <AddDeleteEditor value={value} onChange={onChange} addLabel="Identifiers / documents to add" deleteLabel="Identifiers / documents to remove"
      makeRow={emptyIdentifierRow}
      renderRow={(row, change) => (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Identifier type" value={row.identifier_type} placeholder="e.g. pan, aadhaar, passport" onChange={(e) => change({ ...row, identifier_type: e.target.value })} />
            <FormField label="Identifier / reference number" value={row.identifier_number} onChange={(e) => change({ ...row, identifier_number: e.target.value })} />
            <FormField label="Issue date (optional)" type="date" value={row.issue_date} onChange={(e) => change({ ...row, issue_date: e.target.value })} />
            <FormField label="Expiry date (optional)" type="date" value={row.expiry_date} onChange={(e) => change({ ...row, expiry_date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-[var(--ink-muted)]">Supporting file (optional)</Label>
            <input type="file" className="block w-full rounded-md border p-2 text-xs"
              onChange={(e) => readFileAsDoc(e.target.files?.[0], (doc) => change({ ...row, ...doc }))} />
            {row.file_name && <p className="text-[11px] text-emerald-700">{row.file_name} ready to send</p>}
          </div>
        </div>
      )} />
  );
}

// ---------------------------------------------------------------- holding_nature (update_holding_nature)
// update_holding_nature(client_code, *, member_code="", holding_nature, holders=None)
// holders[]: {rank, pan, occ_code, person:{...}, contact:{phone,email}} -- these are
// the HOLDERS BEING ADDED (rank 2/3), not the existing primary holder.
export function emptyHoldingHolderRow(rank = "2") {
  return {
    _id: nextRowId(), rank, pan: "", occ_code: "01",
    person: { first_name: "", middle_name: "", last_name: "", dob: "", gender: "M" },
    contact: { phone: "", email: "" },
  };
}
export function emptyHoldingNatureSpec() {
  return { holding_nature: "JO", holders: [emptyHoldingHolderRow("2")] };
}
function HoldingHolderFields({ row, onChange }) {
  const setPerson = (key) => (e) => onChange({ ...row, person: { ...row.person, [key]: e.target.value } });
  const setContact = (key) => (e) => onChange({ ...row, contact: { ...row.contact, [key]: e.target.value } });
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormSelect label="Holder rank" value={row.rank} options={[{ value: "2", label: "2 — Second" }, { value: "3", label: "3 — Third" }]}
          onChange={(e) => onChange({ ...row, rank: e.target.value })} />
        <FormSelect label="Occupation" value={row.occ_code} options={OCCUPATION} onChange={(e) => onChange({ ...row, occ_code: e.target.value })} />
        <FormField label="PAN (optional)" value={row.pan} maxLength={10} onChange={(e) => onChange({ ...row, pan: e.target.value.toUpperCase() })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="First name" value={row.person.first_name} onChange={setPerson("first_name")} />
        <FormField label="Middle name" value={row.person.middle_name} onChange={setPerson("middle_name")} />
        <FormField label="Last name" value={row.person.last_name} onChange={setPerson("last_name")} />
        <FormField label="Date of birth" type="date" value={row.person.dob} onChange={setPerson("dob")} />
        <FormSelect label="Gender" value={row.person.gender} options={GENDER} onChange={setPerson("gender")} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Phone" value={row.contact.phone} onChange={setContact("phone")} help="Required by BSE for each added holder." />
        <FormField label="Email (optional)" type="email" value={row.contact.email} onChange={setContact("email")} />
      </div>
    </div>
  );
}
export function HoldingNatureForm({ value, onChange }) {
  const single = value.holding_nature === "SI";
  return (
    <div className="space-y-4">
      <FormSelect label="New holding nature" value={value.holding_nature} options={HOLDING}
        onChange={(e) => onChange({
          holding_nature: e.target.value,
          holders: e.target.value === "SI" ? [] : (value.holders.length ? value.holders : [emptyHoldingHolderRow("2")]),
        })} />
      {single ? (
        <p className="text-xs text-[var(--ink-muted)]">Converting to Single holding sends no additional holders.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Holders being added</h3>
            {value.holders.length < 2 && <AddRowButton label="Add holder" onClick={() => onChange({ ...value, holders: [...value.holders, emptyHoldingHolderRow(String(value.holders.length + 2))] })} />}
          </div>
          <div className="space-y-4">
            {value.holders.map((row) => (
              <RowShell key={row._id} title={`Holder rank ${row.rank}`} onRemove={() => onChange({ ...value, holders: value.holders.filter((r) => r._id !== row._id) })}>
                <HoldingHolderFields row={row} onChange={(next) => onChange({ ...value, holders: value.holders.map((r) => (r._id === row._id ? next : r)) })} />
              </RowShell>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- registry + spec transforms
export const MODE_META = {
  bank: { label: "Bank accounts (add / remove)", Form: BankForm, empty: emptyBankSpec },
  depository: { label: "Depository / demat accounts (add / remove)", Form: DepositoryForm, empty: emptyDepositorySpec },
  contact: { label: "Contact details", Form: ContactForm, empty: emptyContactSpec },
  fatca: { label: "FATCA declaration", Form: FatcaForm, empty: emptyFatcaSpec },
  nominee: { label: "Nominees", Form: NomineeForm, empty: emptyNomineeSpec },
  comm_addr: { label: "Communication address", Form: CommAddrForm, empty: emptyCommAddrSpec },
  foreign_addr: { label: "Foreign address", Form: ForeignAddrForm, empty: emptyForeignAddrSpec },
  holder: { label: "Holder / person details", Form: HolderForm, empty: emptyHolderSpec },
  identifier: { label: "Identifiers / documents (add / remove)", Form: IdentifierForm, empty: emptyIdentifierSpec },
  holding_nature: { label: "Holding nature (Single / Joint / Anyone-or-Survivor)", Form: HoldingNatureForm, empty: emptyHoldingNatureSpec },
};

// Strips internal row ids and empty leaves so the built spec matches exactly
// what the backend builder expects -- never send `_id` or blank optional fields.
export function cleanDeep(value) {
  if (Array.isArray(value)) {
    const items = value.map(cleanDeep).filter((item) => item !== undefined);
    return items.length ? items : undefined;
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value)
      .filter(([key]) => key !== "_id")
      .map(([key, item]) => [key, cleanDeep(item)])
      .filter(([, item]) => item !== undefined);
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  if (value === "" || value === null || value === undefined) return undefined;
  return value;
}

export function buildSpec(mode, spec) {
  switch (mode) {
    case "bank":
    case "depository":
    case "identifier": {
      const out = {};
      const add = cleanDeep(spec.add);
      const del = cleanDeep(spec.delete);
      if (add) out.add = add;
      if (del) out.delete = del;
      return out;
    }
    case "contact":
      return cleanDeep({ holder_rank: spec.holder_rank, occ_code: spec.occ_code, contact: spec.contact }) || {};
    case "fatca":
      return cleanDeep({ holder_rank: spec.holder_rank, pan: spec.pan, person: spec.person, fatca_extra: spec.fatca_extra }) || {};
    case "nominee":
      return cleanDeep({
        holder_rank: spec.holder_rank, occ_code: spec.occ_code, nominee_soa: spec.nominee_soa,
        nominees: spec.nominees.map((row) => ({ ...row, guardian: row.is_minor ? row.guardian : undefined })),
      }) || {};
    case "comm_addr":
    case "foreign_addr":
      return cleanDeep({ address: spec.address }) || {};
    case "holder":
      // make_contact() unconditionally requires phone, so an email-only contact would blow up
      // the builder -- only attach contact when phone is present (validateSpec blocks the
      // email-without-phone combination before this ever runs).
      return cleanDeep({
        holder_rank: spec.holder_rank, occ_code: spec.occ_code, pan: spec.pan, person: spec.person,
        contact: spec.contact?.phone ? spec.contact : undefined,
        include_fatca: spec.include_fatca || undefined,
        fatca_extra: spec.include_fatca ? spec.fatca_extra : undefined,
      }) || {};
    case "holding_nature":
      return cleanDeep({
        holding_nature: spec.holding_nature,
        holders: spec.holding_nature === "SI" ? undefined : spec.holders,
      }) || {};
    default:
      return {};
  }
}

// Client-side gate for the truly required (no-default) builder kwargs, so a
// missing field surfaces as a plain-English message here instead of a 400
// "Bad spec" / payload-build failure from the backend.
export function validateSpec(mode, spec) {
  const rowHas = (row, keys) => keys.every((key) => String(row[key] || "").trim());
  switch (mode) {
    case "bank":
      if (!spec.add.length && !spec.delete.length) return "Add at least one bank account to add or remove.";
      if ([...spec.add, ...spec.delete].some((row) => !rowHas(row, ["ifsc", "acc_num"]))) return "Every bank row needs an IFSC and account number.";
      return "";
    case "depository":
      if (!spec.add.length && !spec.delete.length) return "Add at least one depository account to add or remove.";
      if ([...spec.add, ...spec.delete].some((row) => !rowHas(row, ["dp_id", "client_id"]))) return "Every depository row needs a DP ID and client ID.";
      return "";
    case "identifier":
      if (!spec.add.length && !spec.delete.length) return "Add at least one identifier to add or remove.";
      if ([...spec.add, ...spec.delete].some((row) => !rowHas(row, ["identifier_type", "identifier_number"]))) return "Every identifier row needs a type and a number.";
      return "";
    case "contact":
      if (!spec.contact.phone.trim()) return "A phone / contact number is required.";
      if (["RE", "OF"].includes(spec.contact.contact_type) && (!spec.contact.extension.trim() || !spec.contact.fax_no.trim())) return "Residential/office contacts require an extension and fax number.";
      return "";
    case "fatca":
      if (!spec.pan.trim()) return "PAN is required.";
      if (!spec.person.first_name.trim()) return "The holder's first name is required.";
      if (!spec.person.allow_blank_dob && !spec.person.dob.trim()) return "Date of birth is required (or mark it unknown).";
      return "";
    case "nominee": {
      if (!spec.nominees.length) return "Add at least one nominee.";
      const bad = spec.nominees.find((row) => !row.first_name.trim() || !row.dob.trim() || !row.phone.trim() || !row.comm.line1.trim() ||
        (row.is_minor && !(row.guardian.first_name.trim() && row.guardian.pan.trim())));
      if (bad) return "Every nominee needs a first name, date of birth, phone and address line 1; minors also need a guardian name and PAN.";
      return "";
    }
    case "comm_addr":
      if (!spec.address.line1.trim()) return "Address line 1 is required.";
      return "";
    case "foreign_addr":
      if (!spec.address.line1.trim() || !spec.address.country.trim()) return "Address line 1 and country are required.";
      return "";
    case "holder":
      if (!spec.person.first_name.trim()) return "First name is required.";
      if (!spec.person.allow_blank_dob && !spec.person.dob.trim()) return "Date of birth is required (or mark it unknown).";
      if (spec.contact.email.trim() && !spec.contact.phone.trim()) return "A contact needs a phone number too — an email-only contact cannot be sent.";
      return "";
    case "holding_nature": {
      if (spec.holding_nature !== "SI") {
        if (!spec.holders.length) return "Add at least one holder for Joint / Anyone-or-Survivor holding.";
        const bad = spec.holders.find((row) => !row.person.first_name.trim() || !row.person.dob.trim() || !row.contact.phone.trim());
        if (bad) return "Every added holder needs a first name, date of birth and phone number.";
      }
      return "";
    }
    default:
      return "";
  }
}
